import React, { useState, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './App.css';

function App() {
  const [activeFaq, setActiveFaq] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger);

    gsap.fromTo('.hero-heading', { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.5, ease: 'elastic.out(1, 0.5)' });
    gsap.fromTo('.hero-subheading', { opacity: 0, x: -50 }, { opacity: 1, x: 0, duration: 1.2, delay: 0.5, ease: 'power3.out' });
    gsap.fromTo('.hero-image', { opacity: 0, scale: 0.9 }, { opacity: 1, scale: 1, duration: 1.5, delay: 1, ease: 'back.out(1.7)' });

    gsap.utils.toArray('.section').forEach((section) => {
      gsap.fromTo(section, { opacity: 0, y: 50 }, { opacity: 1, y: 0, duration: 1.2, ease: 'power3.out', scrollTrigger: { trigger: section, start: 'top 80%' } });
    });

    gsap.utils.toArray('.feature-item, .roadmap-item, .faq-item, .gallery-item').forEach((item) => {
      item.addEventListener('mouseenter', () => gsap.to(item, { scale: 1.02, boxShadow: '0 0 15px #FF2E8B', duration: 0.3 }));
      item.addEventListener('mouseleave', () => gsap.to(item, { scale: 1, boxShadow: '0 0 10px #00C4B4', duration: 0.3 }));
    });

    gsap.to('.navbar-logo', { boxShadow: '0 0 15px #FF2E8B', duration: 1.5, repeat: -1, yoyo: true });
    gsap.fromTo('.disco-particle', { y: -20, opacity: 0 }, { y: 20, opacity: 0.5, duration: 2, repeat: -1, yoyo: true, stagger: 0.2 });
    gsap.fromTo('.gallery-item', { opacity: 0, scale: 0.8, rotation: 360 }, { opacity: 1, scale: 1, rotation: 0, duration: 2, stagger: 0.3, ease: 'elastic.out(1, 0.5)', scrollTrigger: { trigger: '.gallery-section', start: 'top 85%' } });
  }, []);

  const toggleFaq = (index) => {
    setActiveFaq(activeFaq === index ? null : index);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
    if (!isSidebarOpen) {
      gsap.to('.sidebar', { x: 0, duration: 0.5, ease: 'power3.out' });
    } else {
      gsap.to('.sidebar', { x: '-100%', duration: 0.5, ease: 'power3.in' });
    }
  };

  const contractAddress = "0xDINOJAM123...789";

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(err => console.error('Failed to copy:', err));
  };

  return (
    <div className="App">
      <nav className="navbar">
        <div className="navbar-logo">
          <img src="/logo.jpg" alt="DinoJam Logo" />
        </div>
        <ul className="navbar-links">
          <li><a href="#home">Home</a></li>
          <li><a href="#about">About</a></li>
          <li><a href="#features">Features</a></li>
          <li><a href="#roadmap">Roadmap</a></li>
          <li><a href="#gallery">Gallery</a></li>
          <li><a href="#faq">FAQ</a></li>
        </ul>
        <div className="hamburger" onClick={toggleSidebar}>
          {isSidebarOpen ? '✕' : '☰'}
        </div>
        <div className="sidebar">
          <ul className="sidebar-links">
            <li><a href="#home" onClick={toggleSidebar}>Home</a></li>
            <li><a href="#about" onClick={toggleSidebar}>About</a></li>
            <li><a href="#features" onClick={toggleSidebar}>Features</a></li>
            <li><a href="#roadmap" onClick={toggleSidebar}>Roadmap</a></li>
            <li><a href="#gallery" onClick={toggleSidebar}>Gallery</a></li>
            <li><a href="#faq" onClick={toggleSidebar}>FAQ</a></li>
          </ul>
        </div>
      </nav>

      <section className="hero section" id="home">
        <h1 className="hero-heading">$DINOJAM</h1>
        <h2 className="hero-subheading">Dino Disco Jammers</h2>
        <img src="/hero.jpg" alt="Dino Disco Jammers" className="hero-image" />
      </section>

      <section className="section about-section" id="about">
        <div className="container">
          <h2 className="section-title">The Funky Rebellion</h2>
          <p className="section-content">
            On the Base chain, the Dino Disco Jammers—neon-clad dinosaurs like T-Rexes in bell-bottoms and Velociraptors with afros—throw the wildest dance party in crypto. Led by DJ Dino Funk, a Stegosaurus with a disco ball tail, $DINOJAM is their groove token to “jam” portfolios to the moon.
          </p>
          <div className="contract-address-container">
            <p className="section-content">Contract Address:</p>
            <div className="contract-address-wrapper">
              <input type="text" value={contractAddress} readOnly className="contract-address" />
              <button className="copy-button" onClick={() => copyToClipboard(contractAddress)}>
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="section features-section" id="features">
        <div className="container">
          <h2 className="section-title">Features</h2>
          <div className="features-grid">
            <div className="feature-item">
              <h3>Disco Dance-Off Hub</h3>
              <p>Join the community on X or Discord to share memes, GIFs, and dance-off videos.</p>
            </div>
            <div className="feature-item">
              <h3>Jammer Spotlight</h3>
              <p>Post-launch, vote for the best disco meme weekly for a shoutout on X.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section roadmap-section" id="roadmap">
        <div className="container">
          <h2 className="section-title">Roadmap</h2>
          <div className="roadmap-timeline">
            <div className="roadmap-item">
              <h3>Q2 2025</h3>
              <p>Launch on Base chain via Cliza Systems, token distribution, Disco Dance-Off Hub rollout.</p>
            </div>
            <div className="roadmap-item">
              <h3>Q3 2025</h3>
              <p>Introduce Jammer Spotlight for community creativity.</p>
            </div>
            <div className="roadmap-item">
              <h3>Q4 2025</h3>
              <p>X hype with disco meme contests for viral traction.</p>
            </div>
            <div className="roadmap-item">
              <h3>2026</h3>
              <p>Explore “Dino Disco Night” event with Base chain meme tokens.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="section gallery-section" id="gallery">
        <div className="container">
          <h2 className="section-title">Gallery</h2>
          <div className="gallery-grid">
            <div className="gallery-item">
              <img src="/gallery.jpg" alt="Dino Dance-Off" />
            </div>
            <div className="gallery-item">
              <img src="/gallery1.jpg" alt="DJ Dino Funk" />
            </div>
            <div className="gallery-item">
              <img src="/gallery2.jpg" alt="Dino Disco Party" />
            </div>
          </div>
        </div>
      </section>

      <section className="section faqs-section" id="faq">
        <div className="container">
          <h2 className="section-title">FAQ</h2>
          <div className="section-content">
            <div className={`faq-item ${activeFaq === 0 ? 'active' : ''}`} onClick={() => toggleFaq(0)}>
              <h3 className="faq-question">What is $DINOJAM?</h3>
              <p className="faq-answer">$DINOJAM is a meme token on Base chain, blending disco vibes with dinosaur absurdity for a funky crypto party.</p>
            </div>
            <div className={`faq-item ${activeFaq === 1 ? 'active' : ''}`} onClick={() => toggleFaq(1)}>
              <h3 className="faq-question">How does Disco Dance-Off Hub work?</h3>
              <p className="faq-answer">It’s a community space on X/Discord to share memes, GIFs, and dance-off videos to keep the party vibe alive.</p>
            </div>
            <div className={`faq-item ${activeFaq === 2 ? 'active' : ''}`} onClick={() => toggleFaq(2)}>
              <h3 className="faq-question">What’s the Jammer Spotlight?</h3>
              <p className="faq-answer">A post-launch feature where the community votes on the best weekly disco meme for an X shoutout.</p>
            </div>
            <div className={`faq-item ${activeFaq === 3 ? 'active' : ''}`} onClick={() => toggleFaq(3)}>
              <h3 className="faq-question">Why launch on Base chain via Cliza Systems?</h3>
              <p className="faq-answer">Base chain via Cliza Systems offers low fees, fast transactions, and a meme coin culture perfect for $DINOJAM’s vibe.</p>
            </div>
          </div>
        </div>
      </section>

      <footer className="footer">
        <div className="social-links">
          <a href="https://x.com/dinojam" target="_blank" rel="noopener noreferrer" className="social-button">X</a>
          <a href="https://t.me/dinojam" target="_blank" rel="noopener noreferrer" className="social-button">Telegram</a>
          <a href="https://discord.gg/dinojam" target="_blank" rel="noopener noreferrer" className="social-button">Discord</a>
        </div>
        <p className="copyright">© 2025 $DINOJAM. All rights reserved.</p>
      </footer>
      <div className="disco-particle"></div>
      <div className="disco-particle"></div>
      <div className="disco-particle"></div>
    </div>
  );
}

export default App;